import type { Map as MapLibreMap } from "maplibre-gl";
import { LatLng } from "../model/LatLng";
import type { StopAnchorWire, TripDelayWire } from "../types/dtos/realtime-api/realtime.dto";

/*
 * ---------------------------------------------------------------------------
 *  Shared schedule / geometry helpers (DOM labels live in transitPath/stopLabels)
 * ---------------------------------------------------------------------------
 */

/**
 * Formats seconds-since-midnight into a local HH:MM string.
 *
 * @param secondsSinceMidnight Arrival or departure time in seconds since midnight.
 * @returns Time string formatted as HH:MM.
 */
export function formatSecondsToHHMM( secondsSinceMidnight: number ): string {
    const hours = Math.floor( secondsSinceMidnight / 3600 ) % 24;
    const minutes = Math.floor( ( secondsSinceMidnight % 3600 ) / 60 );

    return `${ String( hours ).padStart( 2, "0" ) }:${ String( minutes ).padStart( 2, "0" ) }`;
}

const SECONDS_PER_DAY = 86400;

/**
 * Wraps a possibly negative or multi-day offset into [0, 86400) for stable clock display.
 */
function normalizeSecondsSinceMidnight( seconds: number ): number {
    return ( ( seconds % SECONDS_PER_DAY ) + SECONDS_PER_DAY ) % SECONDS_PER_DAY;
}

/**
 * Scheduled arrival (seconds since midnight) plus realtime delay, normalized to one day.
 * Use this for vehicle popup HH:mm:ss and map stop labels so both stay aligned.
 *
 * @param scheduledArrSeconds GTFS scheduled arrival in seconds since midnight.
 * @param delaySec Signed delay in seconds (positive = late).
 * @returns Seconds since midnight in [0, 86400) for the predicted arrival.
 */
export function getNormalizedArrivalSecondsWithDelay(
    scheduledArrSeconds: number,
    delaySec: number,
): number {
    return normalizeSecondsSinceMidnight( scheduledArrSeconds + delaySec );
}

/**
 * Returns per-stop delay when available, otherwise falls back to global delay.
 *
 * @param delay Optional vehicle-trip delay payload.
 * @param stopSequence Stop sequence number.
 * @returns Delay in seconds (signed).
 */
export function getDelayForStopSequence(
    delay: TripDelayWire | undefined,
    stopSequence: number,
): number {
    if ( delay === undefined ) {
        return 0;
    }
    for ( const [ seq, stopDelay ] of delay.stops ) {
        if ( seq === stopSequence ) {
            return stopDelay;
        }
    }

    return delay.global;
}

/**
 * Selects the next stop index using delay-aware predicted **departures**.
 *
 * Using arrival here breaks the first leg: GTFS often has arrival before
 * departure at the origin (dwell). Once `now` passes `arr`, the bus is still at the stop until
 * `dep`, but arrival-based logic skips to the next stop and the pink “current
 * segment” polyline runs toward stop 2 while the vehicle should still be at the
 * terminus (same issue for dwell at any intermediate stop).
 *
 * @param stops Ordered vehicle-trip stops.
 * @param delay Optional delay payload.
 * @param nowSeconds Current local seconds since midnight.
 * @returns Index of next stop, or -1 when none remains for today.
 */
export function findNextStopIndexDelayAware(
    stops: readonly StopAnchorWire[],
    delay: TripDelayWire | undefined,
    nowSeconds: number,
): number {
    const nowNorm = normalizeSecondsSinceMidnight( nowSeconds );

    for ( let i = 0; i < stops.length; i += 1 ) {
        const stop = stops[i];
        const delaySec = getDelayForStopSequence( delay, stop.seq );
        const predictedDeparture = getNormalizedArrivalSecondsWithDelay( stop.dep, delaySec );
        if ( predictedDeparture >= nowNorm ) {
            return i;
        }
    }

    return -1;
}

/**
 * Scheduled arrival at a stop (seconds since midnight) plus realtime delay offset,
 * formatted as HH:MM — same semantics as the vehicle popup clock.
 *
 * @param scheduledArrSeconds GTFS scheduled arrival in seconds since midnight.
 * @param delaySec Signed delay in seconds (positive = late).
 * @returns Local HH:MM for the predicted arrival.
 */
export function formatScheduleArrivalWithDelayToHHMM(
    scheduledArrSeconds: number,
    delaySec: number,
): string {
    return formatSecondsToHHMM( getNormalizedArrivalSecondsWithDelay( scheduledArrSeconds, delaySec ) );
}

/**
 * Formats an ISO 8601 date string into a local HH:MM string.
 *
 * @param isoString ISO timestamp (e.g. from `ItineraryLegStepDTO.time`).
 * @returns Time string formatted as HH:MM, or null when the input is invalid.
 */
export function formatIsoToHHMM( isoString: string | undefined ): string | null {
    if ( isoString === undefined ) {
        return null;
    }
    const ms = new Date( isoString ).getTime();
    if ( Number.isNaN( ms ) ) {
        return null;
    }
    const date = new Date( ms );

    return `${ String( date.getHours() ).padStart( 2, "0" ) }:${ String( date.getMinutes() ).padStart( 2, "0" ) }`;
}

/**
 * Computes the delay in seconds between the real-time and scheduled ISO timestamps
 * found on itinerary steps.
 *
 * @param timeIso Real-time arrival/departure ISO string.
 * @param scheduledTimeIso Scheduled arrival/departure ISO string.
 * @returns Signed delay in seconds (positive = late), or 0 when data is missing.
 */
export function computeItineraryStepDelaySec(
    timeIso: string | undefined,
    scheduledTimeIso: string | undefined,
): number {
    if ( timeIso === undefined || scheduledTimeIso === undefined ) {
        return 0;
    }
    const realMs = new Date( timeIso ).getTime();
    const scheduledMs = new Date( scheduledTimeIso ).getTime();
    if ( Number.isNaN( realMs ) || Number.isNaN( scheduledMs ) ) {
        return 0;
    }

    return ( realMs - scheduledMs ) / 1000;
}

/**
 * Type guard ensuring a stop name exists and contains visible text.
 *
 * @param name Candidate stop name.
 * @returns True when the value is defined and not blank.
 */
export const hasNonEmptyName = ( name: string | undefined ): name is string => {
    return name !== undefined && name.trim().length > 0;
};

/**
 * Validates geographic coordinate values.
 *
 * @param lat Latitude value.
 * @param lon Longitude value.
 * @returns True when both coordinates are finite numbers.
 */
export const isValidCoordinate = ( lat: number, lon: number ): boolean => {
    return Number.isFinite( lat ) && Number.isFinite( lon );
};

/**
 * Computes squared planar distance in lng/lat space.
 *
 * @param a First point.
 * @param b Second point.
 * @returns Squared distance (used for fast comparisons without sqrt).
 */
const squaredDistanceLngLat = ( a: LatLng, b: LatLng ): number => {
    const dx = a.lng - b.lng;
    const dy = a.lat - b.lat;

    return dx * dx + dy * dy;
};

/**
 * Closest point on segment AB to P (planar lng/lat).
 *
 * @param p Query point.
 * @param a Segment start.
 * @param b Segment end.
 * @returns Closest projected point clamped to segment bounds.
 */
const closestPointOnSegment = ( p: LatLng, a: LatLng, b: LatLng ): LatLng => {
    const abx = b.lng - a.lng;
    const aby = b.lat - a.lat;
    const apx = p.lng - a.lng;
    const apy = p.lat - a.lat;
    const abLenSq = abx * abx + aby * aby;

    if ( abLenSq === 0 ) {
        return a;
    }

    let t = ( apx * abx + apy * aby ) / abLenSq;
    t = Math.max( 0, Math.min( 1, t ) );

    return new LatLng( a.lat + t * aby, a.lng + t * abx );
};

/**
 * Projects a point onto a polyline.
 *
 * @param p Query point.
 * @param path Polyline vertices.
 * @returns Closest point on the polyline.
 */
export const nearestPointOnPolyline = ( p: LatLng, path: readonly LatLng[] ): LatLng => {
    if ( path.length === 0 ) {
        return p;
    }

    if ( path.length === 1 ) {
        return path[0];
    }

    let best = path[0];
    let bestD = squaredDistanceLngLat( p, best );

    for ( let i = 0; i < path.length - 1; i += 1 ) {
        const q = closestPointOnSegment( p, path[i], path[i + 1] );
        const d = squaredDistanceLngLat( p, q );

        if ( d < bestD ) {
            bestD = d;
            best = q;
        }
    }

    return best;
};

/*
 * ---------------------------------------------------------------------------
 *  Itinerary walk-leg map symbols
 * ---------------------------------------------------------------------------
 */

/** SDF sprite id used by itinerary walk legs (`symbol-placement: line`). */
export const ITINERARY_WALK_DOT_IMAGE_ID = "itinerary-walk-dot-sdf";

const ITINERARY_WALK_DOT_IMAGE_SIZE_PX = 32;

/**
 * Registers a reusable SDF circle sprite for walk-leg dot symbols.
 * Safe to call repeatedly; the image is added only once per map instance.
 *
 * @param map Active MapLibre map instance.
 */
export function ensureItineraryWalkDotMapImage( map: MapLibreMap ): void {
    if ( map.hasImage( ITINERARY_WALK_DOT_IMAGE_ID ) ) {
        return;
    }

    const canvas = document.createElement( "canvas" );
    canvas.width = ITINERARY_WALK_DOT_IMAGE_SIZE_PX;
    canvas.height = ITINERARY_WALK_DOT_IMAGE_SIZE_PX;

    const context = canvas.getContext( "2d" );
    if ( context == null ) {
        return;
    }

    context.clearRect( 0, 0, ITINERARY_WALK_DOT_IMAGE_SIZE_PX, ITINERARY_WALK_DOT_IMAGE_SIZE_PX );
    context.fillStyle = "#000000";
    context.beginPath();
    context.arc(
        ITINERARY_WALK_DOT_IMAGE_SIZE_PX / 2,
        ITINERARY_WALK_DOT_IMAGE_SIZE_PX / 2,
        ITINERARY_WALK_DOT_IMAGE_SIZE_PX / 2 - 4,
        0,
        Math.PI * 2,
    );
    context.fill();

    map.addImage(
        ITINERARY_WALK_DOT_IMAGE_ID,
        context.getImageData( 0, 0, ITINERARY_WALK_DOT_IMAGE_SIZE_PX, ITINERARY_WALK_DOT_IMAGE_SIZE_PX ),
        {
            "sdf":        true,
            "pixelRatio": 2,
        },
    );
}
